// src/pages/motolivraison/livreur-detail.page.jsx
//
// Détail d'un livreur MotoLivraison avec ses informations + permis + photo moto + stats.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Tag, Descriptions, Statistic, Button, message, Space,
  Breadcrumb, Avatar, Spin, Table, Empty, Typography,
} from 'antd';
import {
  UserOutlined, ArrowLeftOutlined, RightOutlined, CheckCircleOutlined,
  StopOutlined, PlayCircleOutlined, ShoppingCartOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motolivraisonService } from '../../common/services';
import dayjs from 'dayjs';

const { Text } = Typography;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

const STATUT_CMD = {
  en_attente_paiement: { c: 'orange', l: 'Attente paiement' },
  payee: { c: 'blue', l: 'Payée' },
  en_attente_livreur: { c: 'gold', l: 'Attente livreur' },
  assignee: { c: 'cyan', l: 'Assignée' },
  recuperee: { c: 'cyan', l: 'Récupérée' },
  en_livraison: { c: 'geekblue', l: 'En livraison' },
  livree: { c: 'green', l: 'Livrée' },
  confirmee: { c: 'green', l: 'Confirmée' },
  soldee: { c: 'success', l: 'Soldée' },
  annulee: { c: 'red', l: 'Annulée' },
};

const LivreurDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [livreur, setLivreur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commandes, setCommandes] = useState([]);
  const [loadingCommandes, setLoadingCommandes] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getLivreurById(id);
      setLivreur(data);
    } catch (e) {
      message.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  // Commandes (missions) du livreur
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingCommandes(true);
      try {
        const r = await motolivraisonService.listCommandes({ livreurId: id, limit: 100 });
        if (alive) setCommandes(r?.commandes || []);
      } catch (e) { /* silencieux */ } finally { if (alive) setLoadingCommandes(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  const handleAction = async (action) => {
    try {
      const labels = { validate: 'approuvé', suspend: 'suspendu', reactivate: 'réactivé' };
      if (action === 'validate') await motolivraisonService.validateLivreur(id);
      else if (action === 'suspend') await motolivraisonService.suspendLivreur(id);
      else if (action === 'reactivate') await motolivraisonService.reactivateLivreur(id);
      message.success(`Livreur ${labels[action]}`);
      fetch();
    } catch (e) {
      message.error(e?.message || 'Erreur');
    }
  };

  if (loading || !livreur) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  const profile = livreur.livreurMotoProfile || {};
  const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red' })[s] || 'default';
  const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu' })[s] || s || '—';

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/motolivraison/livreurs">Livreurs</Link></Breadcrumb.Item>
          <Breadcrumb.Item>{livreur.firstName} {livreur.lastName}</Breadcrumb.Item>
        </Breadcrumb>

        <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>
          Retour
        </Button>

        <Card>
          <Row gutter={24}>
            <Col xs={24} md={6} style={{ textAlign: 'center' }}>
              <Avatar size={120} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
              <h2 style={{ marginTop: 16 }}>{livreur.firstName} {livreur.lastName}</h2>
              <Tag color={statusColor(profile.status)} style={{ fontSize: 13, padding: '4px 12px' }}>
                {statusLabel(profile.status)}
              </Tag>
              {profile.status === 'approved' && (
                <div style={{ marginTop: 8 }}>
                  <Tag color={profile.isAvailable ? 'green' : 'default'}>
                    {profile.isAvailable ? '🟢 Disponible' : '⚫ Indisponible'}
                  </Tag>
                </div>
              )}
            </Col>

            <Col xs={24} md={18}>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Téléphone">{livreur.phone}</Descriptions.Item>
                {livreur.email && <Descriptions.Item label="Email">{livreur.email}</Descriptions.Item>}
                <Descriptions.Item label="Ville">{profile.cityId?.name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Rôle">{livreur.role}</Descriptions.Item>
              </Descriptions>

              <Card size="small" title="Informations livreur" style={{ marginTop: 16 }}>
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="N° de permis de conduire">
                    {profile.numeroPermisConduire || <span style={{ color: '#bfbfbf' }}>—</span>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Plaque d'immatriculation">
                    {profile.plaqueImmatriculation || <span style={{ color: '#bfbfbf' }}>—</span>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Couleur de la moto">
                    {profile.couleurMoto || <span style={{ color: '#bfbfbf' }}>—</span>}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>
        </Card>

        <Card title="Statistiques" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col xs={6}><Statistic title="Total missions" value={profile.stats?.totalMissions || 0} /></Col>
            <Col xs={6}><Statistic title="Complétées" value={profile.stats?.missionsCompletees || 0} valueStyle={{ color: '#52c41a' }} /></Col>
            <Col xs={6}><Statistic title="Annulées" value={profile.stats?.missionsAnnulees || 0} valueStyle={{ color: '#f5222d' }} /></Col>
            <Col xs={6}><Statistic title="Gains totaux" value={profile.stats?.totalGains || 0} suffix=" F" /></Col>
          </Row>
        </Card>

        <Card title={<span><ShoppingCartOutlined /> Commandes ({commandes.length})</span>} style={{ marginTop: 16 }}>
          <Table
            rowKey="_id"
            size="small"
            loading={loadingCommandes}
            dataSource={commandes}
            locale={{ emptyText: <Empty description="Aucune commande livrée" /> }}
            pagination={{ pageSize: 10, showTotal: (t) => `${t} commande(s)` }}
            columns={[
              { title: 'Réf', key: 'ref', render: (_, r) => <Text code>#{String(r._id).slice(-6).toUpperCase()}</Text> },
              { title: 'Commerce', key: 'com', render: (_, r) => r.commerceId?.nom || '—' },
              {
                title: 'Client', key: 'cli',
                render: (_, r) => {
                  const nom = r.clientNom || (r.clientId ? `${r.clientId.firstName || ''} ${r.clientId.lastName || ''}`.trim() : '');
                  return <div><Text>{nom || '—'}</Text><div><Text type="secondary" style={{ fontSize: 12 }}>{r.clientPhone || r.clientId?.phone || ''}</Text></div></div>;
                },
              },
              { title: 'Montant', dataIndex: 'montantTTC', key: 'mt', align: 'right', render: (v) => <Text strong>{fmt(v)} F</Text> },
              { title: 'Statut', dataIndex: 'statut', key: 'st', render: (v) => <Tag color={STATUT_CMD[v]?.c || 'default'}>{STATUT_CMD[v]?.l || v}</Tag> },
              { title: 'Date', dataIndex: 'createdAt', key: 'dt', render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—' },
            ]}
          />
        </Card>

        <Card title="Actions" style={{ marginTop: 16 }}>
          <Space wrap>
            {profile.status === 'pending' && (
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => handleAction('validate')}>
                Approuver
              </Button>
            )}
            {profile.status === 'approved' && (
              <Button danger icon={<StopOutlined />} onClick={() => handleAction('suspend')}>
                Suspendre
              </Button>
            )}
            {profile.status === 'suspended' && (
              <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => handleAction('reactivate')}>
                Réactiver
              </Button>
            )}
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default LivreurDetail;
